import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Link as LinkIcon, Mail, Check } from "lucide-react";
import { api, formatApiErr } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const ROLES = ["admin", "manager", "member", "guest"];

/**
 * Two-tab invite dialog: (1) Invite by email — multi-address + role + personal
 * message; (2) Copy invitation link — creates a per-email link without sending.
 * Honestly reports Resend configuration status; NEVER shows fake "email sent".
 */
export default function InvitePeopleDialog({ open, onOpenChange, onInvited }) {
  const [tab, setTab] = useState("email");   // email | link
  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("member");
  const [department, setDepartment] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(null);
  const [linkResult, setLinkResult] = useState(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmails(""); setRole("member"); setDepartment(""); setMessage("");
      setResults(null); setLinkResult(null); setLinkEmail(""); setCopied(false);
    }
  }, [open]);

  const parsedEmails = useMemo(() => {
    return Array.from(new Set(emails.split(/[,\s\n;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)));
  }, [emails]);

  const submitEmail = async () => {
    if (parsedEmails.length === 0) { toast.error("Enter at least one email."); return; }
    setBusy(true); setResults(null);
    try {
      const { data } = await api.post("/invitations", {
        emails: parsedEmails, role, department: department || undefined,
        personal_message: message || undefined, send_email: true,
      });
      setResults(data);
      setEmailConfigured(data.email_configured);
      const successes = data.results.filter((r) => r.ok).length;
      const sent = data.results.filter((r) => r.ok && r.invitation?.delivery_status === "sent").length;
      const failed = data.results.filter((r) => r.ok && r.invitation?.delivery_status === "failed").length;
      if (successes) {
        track("invitation_created", { count: successes, role });
        if (sent > 0) {
          track("invitation_email_sent", { count: sent });
          toast.success(`Sent ${sent} email invitation${sent > 1 ? "s" : ""}.`);
        } else if (!data.email_configured) {
          toast.info("Invitations created. Email delivery is not configured — share the copy link instead.");
        } else if (failed > 0) {
          track("invitation_email_failed", { count: failed });
          toast.warning("Invitations created, but email delivery failed. Share the copy link instead.");
        } else {
          toast.success("Invitations created.");
        }
      }
      const failures = data.results.filter((r) => !r.ok);
      if (failures.length) {
        toast.warning(`${failures.length} address${failures.length > 1 ? "es" : ""} could not be invited (${failures[0].reason}).`);
      }
      onInvited && onInvited();
    } catch (e) {
      toast.error(formatApiErr(e.response?.data?.detail) || "Could not send invitations.");
    } finally {
      setBusy(false);
    }
  };

  const submitLink = async () => {
    if (!linkEmail) { toast.error("Enter the email this link is for."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invitations/link", {
        email: linkEmail, role, department: department || undefined,
        personal_message: message || undefined,
      });
      if (data.ok) {
        setLinkResult(data.invitation);
        track("invitation_created", { source: "link", role });
      } else if (data.reason === "already_pending") {
        setLinkResult(data.invitation);
        toast.info("An invitation for this email already exists. Copy the current link.");
      } else {
        toast.warning(`Could not create link (${data.reason}).`);
      }
      onInvited && onInvited();
    } catch (e) {
      toast.error(formatApiErr(e.response?.data?.detail) || "Could not create link.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("[invite] clipboard blocked", err);
    }
    setCopied(true);
    track("invitation_link_copied");
    toast.success("Invite link copied.");
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby="invite-desc" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite people</DialogTitle>
          <DialogDescription id="invite-desc">
            Invite teammates by email or copy an invitation link.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b -mx-6 px-6 pb-0">
          <TabButton active={tab === "email"} onClick={() => setTab("email")} icon={Mail} testid="invite-tab-email">Invite by email</TabButton>
          <TabButton active={tab === "link"} onClick={() => setTab("link")} icon={LinkIcon} testid="invite-tab-link">Copy invitation link</TabButton>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department (optional)</Label>
              <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Design" data-testid="invite-department" />
            </div>
          </div>

          {tab === "email" ? (
            <>
              <div>
                <Label>Emails</Label>
                <textarea
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  className="w-full min-h-[84px] rounded-md border border-zinc-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]"
                  placeholder="jane@company.com, sam@company.com"
                  data-testid="invite-emails"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  {parsedEmails.length > 0
                    ? `${parsedEmails.length} recipient${parsedEmails.length > 1 ? "s" : ""} · comma, space or newline separated`
                    : "Comma, space, or newline separated"}
                </div>
              </div>
              <div>
                <Label>Personal message (optional)</Label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  className="w-full min-h-[60px] rounded-md border border-zinc-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]"
                  placeholder="Excited to have you on the team."
                  data-testid="invite-message"
                />
              </div>

              <Button
                onClick={submitEmail}
                disabled={busy || parsedEmails.length === 0}
                data-testid="invite-send-btn"
                className="w-full bg-[var(--brand-primary)] text-white h-11"
              >
                {busy ? "Sending…" : `Send ${parsedEmails.length || ""} invitation${parsedEmails.length === 1 ? "" : "s"}`.trim()}
              </Button>

              {results && (
                <div className="rounded-lg border p-3 space-y-2" data-testid="invite-results">
                  {results.email_configured === false && (
                    <div className="text-[12px] rounded bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1.5">
                      Email delivery is not configured yet. Invitations were still created — copy each link below.
                    </div>
                  )}
                  {results.results.map((r) => (
                    <ResultRow key={r.email} r={r} onCopy={copyLink} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <Label>Recipient email</Label>
                <Input
                  type="email"
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  data-testid="invite-link-email"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Links are scoped to this email and expire in 7 days.
                </div>
              </div>
              <div>
                <Label>Personal message (optional)</Label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  className="w-full min-h-[60px] rounded-md border border-zinc-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]"
                  placeholder="Optional note to include if you also email the link later."
                  data-testid="invite-link-message"
                />
              </div>
              <Button onClick={submitLink} disabled={busy || !linkEmail}
                      data-testid="invite-create-link-btn"
                      className="w-full bg-[var(--brand-primary)] text-white h-11">
                {busy ? "Creating…" : "Create invitation link"}
              </Button>
              {linkResult && (
                <div className="rounded-lg border p-3 space-y-2" data-testid="invite-link-result">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Invitation for {linkResult.invited_email || linkResult.email}</div>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={linkResult.invite_url || linkResult.token} className="text-xs" data-testid="invite-link-url" />
                    <Button size="sm" onClick={() => copyLink(linkResult.invite_url)} data-testid="invite-link-copy-btn"
                            className="bg-[var(--brand-primary)] text-white">
                      {copied ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
                    </Button>
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    Expires {new Date(linkResult.expires_at).toLocaleString()}. Single-use — becomes invalid after acceptance.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 text-sm font-medium py-2.5 border-b-2 -mb-px transition ${
        active ? "border-[var(--brand-primary)] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"
      }`}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}

function ResultRow({ r, onCopy }) {
  if (!r.ok) {
    const reasonCopy = {
      already_member: "Already a member of this workspace.",
      already_pending: "Invitation already pending for this email.",
      invalid_email: "Invalid email address.",
      invalid_role: "That role is not allowed.",
    }[r.reason] || `Could not invite (${r.reason}).`;
    return (
      <div className="text-xs flex items-center justify-between gap-2">
        <span className="text-zinc-700 truncate">{r.email}</span>
        <span className="text-amber-700">{reasonCopy}</span>
        {r.invitation?.invite_url && (
          <Button size="sm" variant="ghost" className="h-7" onClick={() => onCopy(r.invitation.invite_url)}>
            <Copy className="w-3 h-3 mr-1"/> Copy link
          </Button>
        )}
      </div>
    );
  }
  const inv = r.invitation || {};
  const st = inv.delivery_status;
  const label = st === "sent" ? "Email sent"
              : st === "delivered" ? "Delivered"
              : st === "failed" ? "Email failed"
              : st === "bounced" ? "Bounced"
              : st === "complained" ? "Complained"
              : "Created";
  const color = st === "sent" ? "text-sky-700"
              : st === "delivered" ? "text-emerald-700"
              : (st === "failed" || st === "bounced" || st === "complained") ? "text-red-700"
              : "text-zinc-500";
  return (
    <div className="text-xs flex items-center justify-between gap-2">
      <span className="text-zinc-800 truncate">{r.email}</span>
      <span className={color}>{label}</span>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => onCopy(inv.invite_url)}
              data-testid={`invite-result-copy-${r.email}`}>
        <Copy className="w-3 h-3 mr-1"/> Copy link
      </Button>
    </div>
  );
}
